import React, { useState } from 'react';
import api from '../services/api';

const KbisUploadModal = ({ isOpen, onClose, onUpgradeSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
      setExtractedData(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError("Veuillez sélectionner un fichier PDF ou image de votre K-Bis.");
      return;
    }

    setParsing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.upload('/auth/parse-kbis', formData);
      if (res && res.siren) {
        setExtractedData(res);
      } else {
        setError("Impossibilité de lire le SIREN sur ce fichier. Assurez-vous d'envoyer un extrait K-Bis ou RNE officiel clair.");
      }
    } catch (err) {
      console.error("Erreur analyse K-Bis:", err);
      setError("Erreur lors de l'analyse OCR du K-Bis.");
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.upload('/auth/upgrade-kbis', formData);
      if (res && res.success && res.client) {
        setSuccess(`✅ ${res.message}`);
        setTimeout(() => {
          onUpgradeSuccess(res.client);
        }, 2000);
      } else {
        setError(res?.error || "Erreur lors de la mise à niveau de votre compte.");
      }
    } catch (err) {
      console.error("Erreur upgrade K-Bis:", err);
      setError(err.message || "Impossible de finaliser l'immatriculation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      fontFamily: 'Inter, system-ui, sans-serif'
    }}>
      <div style={{
        background: 'white', padding: '32px', borderRadius: '16px',
        maxWidth: '540px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto'
      }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>📄</span> Finaliser mon Dossier avec le K-Bis
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B7280' }}>
            ✕
          </button>
        </div>

        <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5', marginBottom: '24px' }}>
          Téléversez votre extrait K-Bis ou attestation RNE officielle. Notre IA extraira automatiquement votre <strong>SIREN</strong>, votre <strong>Raison Sociale</strong> et configurera votre plan comptable.
        </p>

        {error && (
          <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '20px' }}>
            {success}
          </div>
        )}

        {!extractedData ? (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              border: '2px dashed #D1D5DB', borderRadius: '12px', padding: '30px 20px',
              textAlign: 'center', background: '#F9FAFB', cursor: 'pointer'
            }}>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="kbis-file-input"
              />
              <label htmlFor="kbis-file-input" style={{ cursor: 'pointer', display: 'block' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>📤</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>
                  {file ? file.name : "Cliquez ou glissez votre K-Bis (PDF, PNG, JPG)"}
                </div>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '6px' }}>
                  Taille max 10 Mo
                </div>
              </label>
            </div>

            <button
              onClick={handleAnalyze}
              disabled={!file || parsing}
              style={{
                width: '100%', marginTop: '20px', padding: '14px',
                background: !file || parsing ? '#9CA3AF' : '#1D9E75',
                color: 'white', border: 'none', borderRadius: '8px',
                fontSize: '15px', fontWeight: '600', cursor: !file || parsing ? 'not-allowed' : 'pointer'
              }}
            >
              {parsing ? "Analyse OCR du K-Bis en cours..." : "1. Analyser mon K-Bis"}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ background: '#F3F4F6', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: '#6B7280', marginBottom: '12px' }}>
                🔍 Données Détectées par l'IA :
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                <div>
                  <span style={{ color: '#6B7280' }}>SIREN :</span>
                  <div style={{ fontWeight: '700', color: '#111827' }}>{extractedData.siren || "Non détecté"}</div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Dénomination :</span>
                  <div style={{ fontWeight: '700', color: '#111827' }}>{extractedData.nom}</div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Forme Juridique :</span>
                  <div style={{ fontWeight: '600', color: '#111827' }}>{extractedData.forme_juridique}</div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Code NAF / APE :</span>
                  <div style={{ fontWeight: '600', color: '#111827' }}>{extractedData.code_naf}</div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Capital Social :</span>
                  <div style={{ fontWeight: '600', color: '#111827' }}>{extractedData.capital_social} €</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setExtractedData(null)}
                style={{
                  flex: 1, padding: '12px', background: 'white',
                  border: '1px solid #D1D5DB', borderRadius: '8px',
                  fontSize: '14px', color: '#374151', cursor: 'pointer'
                }}
              >
                Changer de fichier
              </button>

              <button
                onClick={handleConfirmUpgrade}
                disabled={loading}
                style={{
                  flex: 2, padding: '14px', background: loading ? '#9CA3AF' : '#10B981',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? "Configuration du dossier..." : "2. Valider et Activer mon Compte"}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default KbisUploadModal;
